import React, { useState, useEffect } from 'react';
import { Shield, Calendar, Store, MapPin, MapPinned, Check, X, Loader2, RefreshCw, Trash2, Crosshair, Plus, Briefcase, Repeat, Sparkles } from 'lucide-react';
import { safeFetch, navigate } from './App';
import { isAdmin } from './utils/adminAuth';
import { MultiLocationPicker } from './MultiLocationPicker';
import Tabs from './components/ui/Tabs';

const API_BASE_URL = 'https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default';

export const AdminDashboard = ({ chatId }) => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [processingZone, setProcessingZone] = useState(null);
  const [allZones, setAllZones] = useState([]);
  const [tab, setTab] = useState('zones');
  const [locationModalZone, setLocationModalZone] = useState(null);
  const [locationModalLoading, setLocationModalLoading] = useState(false);
  const [locationModalSaving, setLocationModalSaving] = useState(false);
  const [locationModalPoints, setLocationModalPoints] = useState([]);
  const [locationModalCenter, setLocationModalCenter] = useState(null);

  // Check if user is admin
  const userIsAdmin = isAdmin(chatId);

  useEffect(() => {
    if (userIsAdmin) {
      fetchDashboardData();
    } else {
      setError('Unauthorized: Admin access required');
      setLoading(false);
    }
  }, [chatId, userIsAdmin]);

  const fetchDashboardData = async () => {
    try {
      setRefreshing(true);
      const response = await safeFetch(
        `${API_BASE_URL}/admin-dashboard`,
        chatId
      );

      if (!response.ok) {
        throw new Error('Failed to fetch dashboard data');
      }

      const result = await response.json();
      setData(result);
      setError(null);

      // Fetch all zones using zones-and-categories endpoint
      const zonesResponse = await safeFetch(
        `${API_BASE_URL}/zones-and-categories`,
        chatId
      );

      if (zonesResponse.ok) {
        const zonesData = await zonesResponse.json();
        setAllZones(zonesData.zones || []);
      }
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleZoneAction = async (zoneId, businessId, action) => {
    try {
      setProcessingZone(businessId ? `${zoneId}-${businessId}` : zoneId);

      const response = await safeFetch(
        `${API_BASE_URL}/approve-custom-zone`,
        chatId,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            zone_id: zoneId,
            business_id: businessId,
            action: action, // "approve" or "reject"
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to ${action} zone`);
      }

      // Refresh data after action
      await fetchDashboardData();
    } catch (err) {
      console.error(`Error ${action}ing zone:`, err);
      alert(`שגיאה ב${action === 'approve' ? 'אישור' : 'דחיית'} האזור`);
    } finally {
      setProcessingZone(null);
    }
  };

  const handleRemoveZone = async (zoneId) => {
    if (!confirm(`האם אתה בטוח שברצונך למחוק את האזור "${zoneId}"? פעולה זו תסיר את האזור מכל העסקים המשויכים אליו.`)) {
      return;
    }

    try {
      setProcessingZone(`remove-${zoneId}`);

      const response = await safeFetch(
        `${API_BASE_URL}/remove-zone`,
        chatId,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            zone_id: zoneId,
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to remove zone');
      }

      // Refresh data after removal
      await fetchDashboardData();
      alert(`האזור "${zoneId}" הוסר בהצלחה`);
    } catch (err) {
      console.error('Error removing zone:', err);
      alert(`שגיאה במחיקת האזור`);
    } finally {
      setProcessingZone(null);
    }
  };

  const openLocationModal = async (zoneId) => {
    setLocationModalZone(zoneId);
    setLocationModalLoading(true);
    setLocationModalPoints([]);
    setLocationModalCenter(null);
    try {
      const response = await safeFetch(
        `${API_BASE_URL}/admin-zone-location?zone_id=${encodeURIComponent(zoneId)}`,
        chatId
      );
      if (response.ok) {
        const result = await response.json();
        const pts = (Array.isArray(result.locations) ? result.locations : [])
          .filter((p) => typeof p.latitude === 'number' && typeof p.longitude === 'number')
          .map((p) => ({ lat: p.latitude, lng: p.longitude }));
        setLocationModalPoints(pts);
        if (pts.length > 0) setLocationModalCenter(pts[0]);
      }
    } catch (err) {
      console.error('Error loading zone location:', err);
    } finally {
      setLocationModalLoading(false);
    }
  };

  const closeLocationModal = () => {
    setLocationModalZone(null);
    setLocationModalPoints([]);
    setLocationModalCenter(null);
    setLocationModalSaving(false);
  };

  const addLocationPoint = (point) => {
    setLocationModalPoints((prev) => [...prev, point]);
  };

  const removeLocationPoint = (index) => {
    setLocationModalPoints((prev) => prev.filter((_, i) => i !== index));
  };

  const addCurrentLocationPoint = () => {
    if (!navigator.geolocation) {
      alert('המכשיר אינו תומך באיתור מיקום');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => addLocationPoint({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => alert('לא ניתן לאתר את המיקום הנוכחי')
    );
  };

  const handleSaveZoneLocation = async () => {
    if (!locationModalZone) return;
    try {
      setLocationModalSaving(true);
      const response = await safeFetch(
        `${API_BASE_URL}/admin-set-zone-locations`,
        chatId,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            zone_id: locationModalZone,
            locations: locationModalPoints.map((p) => ({ latitude: p.lat, longitude: p.lng })),
          }),
        }
      );
      if (!response.ok) {
        throw new Error('Failed to update zone locations');
      }
      closeLocationModal();
      alert('מיקומי האזור עודכנו בהצלחה');
    } catch (err) {
      console.error('Error saving zone locations:', err);
      alert('שגיאה בעדכון מיקומי האזור');
      setLocationModalSaving(false);
    }
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('he-IL', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const TYPE_BADGES = {
    business: { label: 'עסק', cls: 'bg-info-100 text-info-700' },
    event: { label: 'מפגש', cls: 'bg-brand-100 text-brand-700' },
    barter: { label: 'ברטר', cls: 'bg-warning-100 text-warning-700' },
    store: { label: 'חנות', cls: 'bg-success-100 text-success-700' },
  };

  const renderEntityCard = (item, badgeType) => {
    const bt = badgeType || item.type || 'business';
    const badge = TYPE_BADGES[bt] || TYPE_BADGES.business;
    const goToStore = badgeType === 'store' && item.store_slug;
    return (
      <div
        key={`${bt}-${item.id}`}
        onClick={() =>
          goToStore
            ? navigate('store', `slug=${item.store_slug}`)
            : navigate('business', `business_id=${item.id}`)
        }
        className="card-glass border border-line rounded-2xl p-4 hover:shadow-lg hover:border-brand-300 cursor-pointer transition-all"
      >
        <div className="flex gap-4">
          {item.image_url && (
            <div className="flex-shrink-0">
              <img
                src={item.image_url}
                alt={item.name}
                className="w-20 h-20 rounded-xl object-cover"
              />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2 mb-2">
              <h3 className="font-bold text-ink-strong text-lg truncate">
                {item.name}
              </h3>
              <span
                className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${badge.cls}`}
              >
                {badge.label}
              </span>
            </div>
            {item.description && (
              <p className="text-ink-muted text-sm mb-2 line-clamp-2">
                {item.description}
              </p>
            )}
            <div className="flex flex-wrap gap-2 mb-2">
              {item.categories?.map((cat, idx) => (
                <span
                  key={idx}
                  className="px-3 py-1.5 bg-warning-500 text-white rounded-lg text-xs font-semibold shadow-sm"
                >
                  {cat}
                </span>
              ))}
            </div>
            <div className="flex items-center justify-between text-xs text-ink-subtle">
              <span>{item.created_at ? formatDate(item.created_at) : ''}</span>
              {item.zones?.length > 0 && (
                <div className="flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  <span>{item.zones.length} אזורים</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const EmptyState = ({ text }) => (
    <div className="card-glass border border-line rounded-2xl p-6 text-center text-ink-subtle">
      {text}
    </div>
  );

  const renderEntityGrid = (items, emptyText, badgeType) =>
    !items || items.length === 0 ? (
      <EmptyState text={emptyText} />
    ) : (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {items.map((it) => renderEntityCard(it, badgeType))}
      </div>
    );

  if (loading) {
    return (
      <div className="min-h-screen bg-surface-base flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-brand-500 animate-spin mx-auto mb-4" />
          <p className="text-ink-muted">טוען נתוני ניהול...</p>
        </div>
      </div>
    );
  }

  if (error || !userIsAdmin) {
    return (
      <div className="min-h-screen bg-surface-base flex items-center justify-center">
        <div className="surface-elevated p-8 max-w-md text-center rounded-2xl">
          <div className="mx-auto w-16 h-16 rounded-pill bg-danger-50 text-danger-600 inline-flex items-center justify-center mb-4">
            <Shield className="w-8 h-8" />
          </div>
          <h2 className="heading-lg mb-2">גישה נדחתה</h2>
          <p className="text-ink-muted">אין לך הרשאות גישה לדף זה</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-base pb-24">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white/80 backdrop-blur-lg border-b border-line shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Shield className="w-8 h-8 text-brand-500" />
              <h1 className="text-2xl font-bold text-ink-strong">לוח בקרה לניהול</h1>
            </div>
            <button
              onClick={fetchDashboardData}
              disabled={refreshing}
              className="flex items-center gap-2 px-4 py-2 bg-brand-500 text-white rounded-xl hover:bg-brand-600 transition-all disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              <span>רענן</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={tab} onChange={setTab} variant="pill" className="space-y-6">
          <div className="overflow-x-auto -mx-4 px-4 pb-1">
            <Tabs.List className="flex-nowrap">
              <Tabs.Trigger value="zones" icon={<MapPin />}>
                כל האזורים ({allZones.length})
              </Tabs.Trigger>
              <Tabs.Trigger value="stores" icon={<Store />}>
                כל החנויות ({data?.all_stores?.length || 0})
              </Tabs.Trigger>
              <Tabs.Trigger value="businesses" icon={<Briefcase />}>
                כל העסקים ({data?.all_businesses?.length || 0})
              </Tabs.Trigger>
              <Tabs.Trigger value="events" icon={<Calendar />}>
                כל המפגשים ({data?.all_events?.length || 0})
              </Tabs.Trigger>
              <Tabs.Trigger value="barters" icon={<Repeat />}>
                כל הברטרים ({data?.all_barters?.length || 0})
              </Tabs.Trigger>
              <Tabs.Trigger value="new" icon={<Sparkles />}>
                חדשים ({data?.new_items?.length || 0})
              </Tabs.Trigger>
              <Tabs.Trigger value="pendingZones" icon={<MapPinned />}>
                אזורים לאישור ({data?.custom_zones_pending?.length || 0})
              </Tabs.Trigger>
            </Tabs.List>
          </div>

          {/* 1. All Zones */}
          <Tabs.Panel value="zones">
            {allZones.length === 0 ? (
              <EmptyState text="אין אזורים במערכת" />
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {allZones.map((zone) => {
                  const zoneId = typeof zone === 'string' ? zone : zone.zone_id;
                  return (
                    <div
                      key={zoneId}
                      className="bg-white border border-line-strong rounded-xl p-4 hover:shadow-lg transition-all"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <MapPin className="w-5 h-5 text-brand-500 flex-shrink-0" />
                          <span className="font-bold text-ink-strong text-base truncate" title={zoneId}>
                            {zoneId}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button
                            onClick={() => openLocationModal(zoneId)}
                            className="flex items-center justify-center px-3 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 transition-all text-sm font-medium"
                            title="קבע מיקום אזור"
                          >
                            <MapPin className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleRemoveZone(zoneId)}
                            disabled={processingZone === `remove-${zoneId}`}
                            className="flex items-center justify-center px-3 py-2 bg-danger-500 text-white rounded-lg hover:bg-danger-600 transition-all text-sm disabled:opacity-50 font-medium"
                            title="מחק אזור"
                          >
                            {processingZone === `remove-${zoneId}` ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Tabs.Panel>

          {/* 2. All Stores */}
          <Tabs.Panel value="stores">
            {renderEntityGrid(data?.all_stores, 'אין חנויות במערכת', 'store')}
          </Tabs.Panel>

          {/* 3. All Businesses */}
          <Tabs.Panel value="businesses">
            {renderEntityGrid(data?.all_businesses, 'אין עסקים במערכת', 'business')}
          </Tabs.Panel>

          {/* 4. All Events */}
          <Tabs.Panel value="events">
            {renderEntityGrid(data?.all_events, 'אין מפגשים במערכת', 'event')}
          </Tabs.Panel>

          {/* 5. All Barters */}
          <Tabs.Panel value="barters">
            {renderEntityGrid(data?.all_barters, 'אין ברטרים במערכת', 'barter')}
          </Tabs.Panel>

          {/* 6. New businesses / events */}
          <Tabs.Panel value="new">
            {renderEntityGrid(
              data?.new_items,
              'אין פריטים חדשים בשבוע האחרון',
              null
            )}
          </Tabs.Panel>

          {/* 7. New zones awaiting approval */}
          <Tabs.Panel value="pendingZones">
            {!data?.custom_zones_pending || data.custom_zones_pending.length === 0 ? (
              <EmptyState text="אין אזורים מותאמים אישית ממתינים לאישור" />
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {data.custom_zones_pending.map((zoneGroup) => (
                  <div
                    key={zoneGroup.zone_id}
                    className="card-glass border border-warning-100 rounded-2xl p-5 bg-warning-50/50"
                  >
                    <div className="flex items-center justify-between mb-4 pb-3 border-b border-warning-100">
                      <div className="flex items-center gap-2 min-w-0">
                        <MapPin className="w-5 h-5 text-warning-600 flex-shrink-0" />
                        <h3 className="font-bold text-ink-strong text-lg truncate">
                          {zoneGroup.zone_id}
                        </h3>
                        <span className="text-xs text-ink-subtle whitespace-nowrap">
                          ({zoneGroup.businesses.length} עסק{zoneGroup.businesses.length > 1 ? 'ים' : ''})
                        </span>
                      </div>
                      <div className="flex gap-2 flex-shrink-0">
                        <button
                          onClick={() => handleZoneAction(zoneGroup.zone_id, null, 'approve')}
                          disabled={processingZone === zoneGroup.zone_id}
                          className="flex items-center gap-1 px-3 py-1.5 bg-success-500 text-white rounded-lg hover:bg-success-600 transition-all text-sm disabled:opacity-50"
                        >
                          {processingZone === zoneGroup.zone_id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Check className="w-4 h-4" />
                          )}
                          <span>אשר אזור</span>
                        </button>
                        <button
                          onClick={() => handleZoneAction(zoneGroup.zone_id, null, 'reject')}
                          disabled={processingZone === zoneGroup.zone_id}
                          className="flex items-center gap-1 px-3 py-1.5 bg-danger-500 text-white rounded-lg hover:bg-danger-600 transition-all text-sm disabled:opacity-50"
                        >
                          {processingZone === zoneGroup.zone_id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <X className="w-4 h-4" />
                          )}
                          <span>דחה אזור</span>
                        </button>
                      </div>
                    </div>
                    {zoneGroup.businesses.length > 0 && (
                      <div className="space-y-3">
                        {zoneGroup.businesses.map((business) => (
                          <div
                            key={business.id}
                            className="bg-white rounded-xl p-4 shadow-sm"
                          >
                            <div className="flex gap-3">
                              {business.image_url && (
                                <img
                                  src={business.image_url}
                                  alt={business.name}
                                  className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
                                />
                              )}
                              <div className="flex-1 min-w-0">
                                <h4 className="font-semibold text-ink-strong mb-1 truncate">
                                  {business.name}
                                </h4>
                                {business.description && (
                                  <p className="text-sm text-ink-muted line-clamp-2">
                                    {business.description}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Tabs.Panel>
        </Tabs>
      </div>

      {locationModalZone && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={closeLocationModal}>
          <div
            className="bg-white rounded-3xl w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            dir="rtl"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-l from-brand-500 to-brand-600 text-white">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-white/20 flex items-center justify-center">
                  <MapPinned className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-bold text-lg leading-tight">מיקומי האזור</h3>
                  <p className="text-white/80 text-sm">{locationModalZone}</p>
                </div>
              </div>
              <button
                onClick={closeLocationModal}
                className="w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center transition-all"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {locationModalLoading ? (
              <div className="h-96 flex items-center justify-center">
                <Loader2 className="w-10 h-10 text-brand-500 animate-spin" />
              </div>
            ) : (
              <div className="flex flex-col lg:flex-row flex-1 min-h-0">
                {/* Map */}
                <div className="relative flex-1 min-h-[340px] lg:min-h-[520px]">
                  <MultiLocationPicker
                    points={locationModalPoints}
                    onAddPoint={addLocationPoint}
                    center={locationModalCenter}
                  />
                  <div className="absolute top-3 inset-x-3 z-[1000] pointer-events-none flex justify-center">
                    <div className="pointer-events-auto bg-white/95 backdrop-blur shadow-lg rounded-full px-4 py-2 text-sm font-medium text-ink-strong flex items-center gap-2">
                      <Plus className="w-4 h-4 text-brand-500" />
                      לחץ על המפה כדי להוסיף מיקום
                    </div>
                  </div>
                </div>

                {/* Sidebar list */}
                <div className="w-full lg:w-80 flex flex-col border-t lg:border-t-0 lg:border-r border-line bg-surface-base/40">
                  <div className="flex items-center justify-between px-5 py-3 border-b border-line">
                    <span className="font-bold text-ink-strong">מיקומים שנבחרו</span>
                    <span className="px-2.5 py-1 rounded-full bg-brand-100 text-brand-700 text-xs font-bold">
                      {locationModalPoints.length}
                    </span>
                  </div>

                  <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2 min-h-[120px] max-h-[300px] lg:max-h-none">
                    {locationModalPoints.length === 0 ? (
                      <div className="h-full flex flex-col items-center justify-center text-center text-ink-subtle py-8">
                        <MapPin className="w-10 h-10 mb-2 opacity-40" />
                        <p className="text-sm">עדיין לא נבחרו מיקומים.</p>
                        <p className="text-xs mt-1">לחץ על המפה כדי להוסיף.</p>
                      </div>
                    ) : (
                      locationModalPoints.map((p, idx) => (
                        <div
                          key={`${p.lat}-${p.lng}-${idx}`}
                          className="flex items-center gap-3 bg-white rounded-xl border border-line p-3 shadow-sm hover:border-brand-300 transition-all"
                        >
                          <div className="w-7 h-7 rounded-full bg-brand-500 text-white text-sm font-bold flex items-center justify-center flex-shrink-0">
                            {idx + 1}
                          </div>
                          <div className="flex-1 min-w-0 leading-tight">
                            <p className="text-sm font-medium text-ink-strong">{p.lat.toFixed(5)}</p>
                            <p className="text-xs text-ink-subtle">{p.lng.toFixed(5)}</p>
                          </div>
                          <button
                            onClick={() => removeLocationPoint(idx)}
                            className="w-8 h-8 rounded-lg text-danger-500 hover:bg-danger-50 flex items-center justify-center transition-all flex-shrink-0"
                            title="הסר מיקום"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))
                    )}
                  </div>

                  <div className="px-4 py-3 border-t border-line">
                    <button
                      onClick={addCurrentLocationPoint}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-brand-300 text-brand-600 hover:bg-brand-50 transition-all text-sm font-medium"
                    >
                      <Crosshair className="w-4 h-4" />
                      הוסף את מיקומי הנוכחי
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="flex items-center justify-between gap-2 px-6 py-4 border-t border-line bg-white">
              <p className="text-xs text-ink-subtle hidden sm:block">
                שמירה תחליף את כל מיקומי האזור הקיימים.
              </p>
              <div className="flex items-center gap-2 mr-auto">
                <button
                  onClick={closeLocationModal}
                  disabled={locationModalSaving}
                  className="px-5 py-2.5 rounded-xl border border-line-strong text-ink-strong hover:bg-surface-base transition-all disabled:opacity-50 font-medium"
                >
                  ביטול
                </button>
                <button
                  onClick={handleSaveZoneLocation}
                  disabled={locationModalSaving || locationModalLoading}
                  className="flex items-center gap-2 px-5 py-2.5 bg-brand-500 text-white rounded-xl hover:bg-brand-600 transition-all disabled:opacity-50 font-medium shadow-lg shadow-brand-500/30"
                >
                  {locationModalSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  <span>שמור {locationModalPoints.length} מיקומים</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
